import { useCallback, useEffect, useRef, useState } from 'react';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { groupTripApi } from '../../api/groupTripApi';
import { hostCommerceApi } from '../../api/hostCommerceApi';
import { normalizeGroupTripList } from '../group-trip/groupTripModel';
import {
  hostCommerceErrorMessage,
  hostCommercePresentationStatuses,
  moneyWithCurrency,
  presentationStatusLabel,
} from './hostCommerceModel';
import '../group-trip/group-trip.css';
import './host-commerce.css';

const PAGE_LIMIT = 20;

export default function HostCommercePage() {
  const { t } = useTravelTranslation();
  const [groupTrips, setGroupTrips] = useState([]);
  const [groupTripId, setGroupTripId] = useState('');
  const [presentationStatus, setPresentationStatus] = useState('');
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const generation = useRef(0);

  const load = useCallback(async ({ cursor = null, append = false } = {}) => {
    const token = ++generation.current;
    if (!append) {
      setPhase('loading');
      setError('');
    }
    try {
      const params = { limit: PAGE_LIMIT };
      if (groupTripId) params.groupTripId = groupTripId;
      if (presentationStatus) params.presentationStatus = presentationStatus;
      if (cursor) params.cursor = cursor;
      const payload = await hostCommerceApi.history(params);
      if (token !== generation.current) return;
      setSummary(payload?.summary ?? null);
      const pageItems = Array.isArray(payload?.items) ? payload.items : [];
      setItems((current) => (append ? [...current, ...pageItems] : pageItems));
      setNextCursor(payload?.nextCursor || null);
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      setError(hostCommerceErrorMessage(cause, t));
      if (!append) setPhase('error');
    }
  }, [groupTripId, presentationStatus, t]);

  useEffect(() => {
    groupTripApi.listMine({ limit: 50 })
      .then((raw) => setGroupTrips(normalizeGroupTripList(raw, t).items))
      .catch(() => setGroupTrips([]));
  }, [t]);

  useEffect(() => {
    load({ append: false });
    return () => { generation.current += 1; };
  }, [load]);

  const totals = [
    ['gross', summary?.grossAmountTotal, t('travel.commerce.earnings.grossHint')],
    ['platform_fee', summary?.platformFeeAmountTotal],
    ['host_receivable', summary?.hostReceivableAmountTotal],
    ['pending_release', summary?.pendingReleaseAmount],
    ['released', summary?.releasedAmount],
    ['refunded', summary?.refundedAmount],
  ];

  return (
    <main className="group-trips host-commerce" aria-labelledby="host-commerce-title">
      <header className="group-trips__intro">
        <p>{t('travel.common.travel')}</p>
        <h1 id="host-commerce-title">{t('travel.commerce.earnings.title')}</h1>
        <span>{t('travel.commerce.earnings.subtitle')}</span>
      </header>

      <form className="host-commerce__filters" onSubmit={(event) => event.preventDefault()}>
        <label htmlFor="host-commerce-group-trip">
          {t('travel.commerce.history.filterGroupTrip')}
          <select
            id="host-commerce-group-trip"
            value={groupTripId}
            onChange={(event) => setGroupTripId(event.target.value)}
          >
            <option value="">{t('travel.commerce.history.filterGroupTripAll')}</option>
            {groupTrips.map((trip) => (
              <option key={trip.groupTripId} value={String(trip.groupTripId)}>{trip.recruitmentTitle}</option>
            ))}
          </select>
        </label>
        <label htmlFor="host-commerce-status">
          {t('travel.commerce.history.filterStatus')}
          <select
            id="host-commerce-status"
            value={presentationStatus}
            onChange={(event) => setPresentationStatus(event.target.value)}
          >
            <option value="">{t('travel.commerce.history.filterStatusAll')}</option>
            {hostCommercePresentationStatuses().map((status) => (
              <option key={status} value={status}>{presentationStatusLabel(status, t)}</option>
            ))}
          </select>
        </label>
      </form>

      {phase === 'loading' ? (
        <section className="group-trips__state"><p>{t('travel.common.loading')}</p></section>
      ) : null}
      {phase === 'error' ? (
        <section className="group-trips__state group-trips__state--error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => load({ append: false })}>{t('travel.common.retry')}</button>
        </section>
      ) : null}

      {phase === 'ready' && summary ? (
        <section className="host-commerce__summary" aria-label={t('travel.commerce.earnings.title')}>
          {totals.map(([key, amount, hint]) => (
            <div key={key}>
              <dt>{t(`travel.commerce.earnings.${key}`)}</dt>
              <dd>{moneyWithCurrency(amount, items[0]?.currencyCode)}</dd>
              {hint ? <p className="group-trips__hint">{hint}</p> : null}
            </div>
          ))}
        </section>
      ) : null}

      {phase === 'ready' && items.length === 0 ? (
        <section className="group-trips__state"><p>{t('travel.commerce.history.empty')}</p></section>
      ) : null}

      {items.length ? (
        <ul className="group-trips__list">
          {items.map((item) => (
            <li className="group-trips__card" key={item.tradeId}>
              <h2>{item.groupTripTitle || t('travel.groupTrip.defaultTitle')}</h2>
              <p>{presentationStatusLabel(item.presentationStatus, t)}</p>
              <p className="group-trips__hint">
                {t('travel.commerce.history.buyer')}
                {': '}
                {item.buyerDisplayName || t('travel.commerce.history.buyerFallback')}
              </p>
              <p>{`${t('travel.commerce.history.gross')}: ${moneyWithCurrency(item.grossAmount, item.currencyCode)}`}</p>
              <p>{`${t('travel.commerce.history.platform_fee')}: ${moneyWithCurrency(item.platformFeeAmount, item.currencyCode)}`}</p>
              <p>{`${t('travel.commerce.history.host_receivable')}: ${moneyWithCurrency(item.hostReceivableAmount, item.currencyCode)}`}</p>
              {item.paidAt ? <p className="group-trips__hint">{t('travel.commerce.history.paidAt')}: {item.paidAt}</p> : null}
              {item.releasedAt ? <p className="group-trips__hint">{t('travel.commerce.history.releasedAt')}: {item.releasedAt}</p> : null}
              {item.refundedAt ? <p className="group-trips__hint">{t('travel.commerce.history.refundedAt')}: {item.refundedAt}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {phase === 'ready' && nextCursor ? (
        <button type="button" onClick={() => load({ cursor: nextCursor, append: true })}>
          {t('travel.common.loadMore')}
        </button>
      ) : null}
    </main>
  );
}
